import {Fragment, useState} from 'react';
import {format} from 'date-fns';
import {AlertTriangle, Folder, FolderX, Loader2} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {trpc, type RouterOutput} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {FTP_SUBFOLDER_ROLE_LABELS, FTP_SUBFOLDER_ROLES} from '@common/groupFtp/types';
import {FolderChip} from '@internal/pages/group-pipeline/detail/ftp/FolderChip';
import {
  FtpFolderPickerDialog,
  type PickerStart,
} from '@internal/pages/group-pipeline/detail/ftp/FtpFolderPickerDialog';

type FolderLink = NonNullable<RouterOutput['internal']['groupFtp']['folder']['link']>;
type Suggestion = RouterOutput['internal']['groupFtp']['suggestions'][number];

// The group's folder on CBS's FTP: linked, or how to link it.
export function FtpFolderCard({vbaGroupId, canLink}: {vbaGroupId: string; canLink: boolean}) {
  const folder = trpc.internal.groupFtp.folder.useQuery({vbaGroupId});
  const [picker, setPicker] = useState<PickerStart | null>(null);

  if (folder.isLoading) return <FolderCardSkeleton />;
  if (!folder.data) return null;
  const {link, ftpConfigured, root} = folder.data;

  return (
    <>
      {link ? (
        <LinkedFolder link={link} onChange={canLink ? () => setPicker({path: link.path}) : null} />
      ) : !ftpConfigured || !root ? (
        <section className='rounded-lg border bg-white px-5 py-4 text-sm text-gray-500'>
          {!ftpConfigured
            ? "CBS's FTP isn't connected in this environment."
            : "CBS's FTP isn't set up for this client yet."}
        </section>
      ) : (
        <NoFolder vbaGroupId={vbaGroupId} canLink={canLink} onPick={setPicker} />
      )}
      {picker && (
        <FtpFolderPickerDialog
          vbaGroupId={vbaGroupId}
          root={root}
          start={picker}
          onOpenChange={(open) => !open && setPicker(null)}
        />
      )}
    </>
  );
}

// Shaped between the linked and not-linked cards, since which one loads isn't known yet.
function FolderCardSkeleton() {
  return (
    <section
      aria-busy='true'
      aria-label='Checking the FTP folder'
      className='flex flex-col gap-4 rounded-lg border bg-white p-5'
    >
      <div className='flex items-start justify-between gap-3'>
        <div className='flex gap-3'>
          <Skeleton className='h-10 w-10 shrink-0 rounded-lg' />
          <div className='flex flex-col gap-2 pt-0.5'>
            <Skeleton className='h-4 w-56' />
            <Skeleton className='h-3 w-40' />
          </div>
        </div>
        <Skeleton className='h-10 w-40' />
      </div>
      <div className='grid auto-rows-[22px] grid-cols-[max-content_1fr] items-center gap-x-6 gap-y-2 border-t pt-4 lg:grid-cols-[max-content_1fr_max-content_1fr]'>
        {[0, 1, 2, 3].map((i) => (
          <Fragment key={i}>
            <Skeleton className='h-4 w-24' />
            <Skeleton className='h-4 w-32' />
          </Fragment>
        ))}
      </div>
    </section>
  );
}

function FolderIcon({missing}: {missing?: boolean}) {
  const Icon = missing ? FolderX : Folder;
  return (
    <span
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
        missing ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-500',
      )}
    >
      <Icon className='h-5 w-5' />
    </span>
  );
}

function LinkedFolder({link, onChange}: {link: FolderLink; onChange: (() => void) | null}) {
  const linkedHow =
    link.source === 'GROUP_FILES'
      ? "Linked from CBS's Group_Files"
      : `Linked${link.linkedByName ? ` by ${link.linkedByName}` : ''} ${format(new Date(link.linkedAt), 'MMM d, yyyy')}`;
  const checked = link.status === 'UNCHECKED' ? "Couldn't reach the FTP to check it" : null;

  return (
    <section className='flex flex-col gap-4 rounded-lg border bg-white p-5'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div className='flex min-w-0 gap-3'>
          <FolderIcon missing={link.status === 'MISSING'} />
          <div className='min-w-0'>
            <p className='break-all font-mono text-sm font-medium'>{link.path}</p>
            <p className='mt-0.5 text-xs text-gray-500'>
              {[linkedHow, checked].filter(Boolean).join(' · ')}
            </p>
            {link.status === 'MISSING' && (
              <p className='mt-1 text-sm text-red-700'>
                This folder isn't on the FTP anymore. It may have been moved or renamed.
              </p>
            )}
          </div>
        </div>
        {onChange && (
          <Button variant='outline' className='bg-white' onClick={onChange}>
            Manage folder mapping
          </Button>
        )}
      </div>
      <dl className='grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 border-t pt-4 text-sm lg:grid-cols-[max-content_1fr_max-content_1fr]'>
        {FTP_SUBFOLDER_ROLES.map((role) => {
          const subs = link.subfolders.filter((s) => s.role === role);
          return (
            <Fragment key={role}>
              <dt className='text-gray-500'>{FTP_SUBFOLDER_ROLE_LABELS[role]}</dt>
              <dd className='flex min-w-0 flex-wrap gap-x-4 gap-y-1'>
                {subs.length === 0 && <span className='text-gray-400'>Not set</span>}
                {subs.map((sub) => (
                  <span key={sub.name} className='flex min-w-0 items-center gap-1'>
                    <FolderChip name={sub.name} />
                    {sub.found === false && (
                      <span className='flex shrink-0 items-center gap-1 text-xs text-amber-800'>
                        <AlertTriangle className='h-3 w-3' />
                        missing on the FTP
                      </span>
                    )}
                  </span>
                ))}
              </dd>
            </Fragment>
          );
        })}
      </dl>
    </section>
  );
}

const suggestionWhy = (s: Suggestion) =>
  s.reason.kind === 'GROUP_FILES'
    ? "Where CBS's Group_Files points"
    : s.reason.kind === 'SAME_NAME'
      ? 'Same name as the group'
      : `Shares "${s.reason.word}" with the group's name`;

function NoFolder({
  vbaGroupId,
  canLink,
  onPick,
}: {
  vbaGroupId: string;
  canLink: boolean;
  onPick: (start: PickerStart) => void;
}) {
  const suggestions = trpc.internal.groupFtp.suggestions.useQuery({vbaGroupId}, {enabled: canLink});

  return (
    <section className='flex flex-col gap-4 rounded-lg border border-dashed border-gray-300 bg-white p-5'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div className='flex gap-3'>
          <FolderIcon />
          <div>
            <p className='text-sm font-semibold'>No FTP folder linked</p>
            <p className='mt-0.5 text-sm text-gray-600'>
              Link the group's folder so documents land where CBS works from them.
            </p>
          </div>
        </div>
        {canLink && (
          <div className='flex gap-2'>
            <Button onClick={() => onPick({})}>Pick folder</Button>
            <Button variant='outline' disabled title='Not available yet'>
              Create folder
            </Button>
          </div>
        )}
      </div>

      {canLink && suggestions.isLoading && (
        <p className='flex items-center gap-2 text-xs text-gray-500'>
          <Loader2 className='h-3.5 w-3.5 animate-spin' />
          Looking for the group's folder
        </p>
      )}
      {suggestions.data && suggestions.data.length > 0 && (
        <div className='flex flex-col gap-2'>
          <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
            Possible matches · confirm the right one
          </p>
          {suggestions.data.map((s) => {
            const missingOnboarding = !s.roles.includes('TO_CONCIERGE');
            return (
              <div
                key={s.path}
                className='flex flex-wrap items-center justify-between gap-2 rounded-md bg-gray-50 px-3 py-2.5'
              >
                <div className='min-w-0'>
                  <p className='break-all font-mono text-sm'>{s.path}</p>
                  <p className='text-xs text-gray-500'>
                    {suggestionWhy(s)}
                    {missingOnboarding && ' · no ToConcierge folder'}
                  </p>
                </div>
                <Button
                  variant='outline'
                  size='sm'
                  className='bg-white'
                  onClick={() =>
                    onPick({
                      path: s.path,
                      source: s.reason.kind === 'GROUP_FILES' ? 'GROUP_FILES' : 'PICKED',
                    })
                  }
                >
                  Link this folder
                </Button>
              </div>
            );
          })}
          <p className='text-xs text-gray-500'>
            Folders often use a DBA or a shortened name, so check before linking.
          </p>
        </div>
      )}
    </section>
  );
}
